import * as React from 'react';
import * as L from 'leaflet';
import { decodePolyline } from '../lib/polyline';
import { distanceToKm, formatDuration } from '../lib/format';
import { routeColor } from '../lib/colors';
import { AvoidPolygon, Waypoint } from '../types';

const { useEffect, useRef } = React;

export interface MapViewProps {
  waypoints: Waypoint[];
  onWaypointDragEnd: (index: number, lon: number, lat: number) => void;
  pickMode: boolean;
  onPick: (lon: number, lat: number) => void;
  routesResponse: any | null;
  matrixResponse: any | null;
  fitBoundsSignal: number;
  clearSignal: number;
  visibleRoutes: boolean[];
  selectedRouteIdx: number;
  onRouteClick: (idx: number) => void;
  // Polygon drawing/editing:
  drawPolygonMode: boolean;
  editingPolygonId: string | null;
  polygonVertices: [number, number][];
  onPolygonVertexAdd: (lon: number, lat: number) => void;
  onPolygonVertexMove: (i: number, lon: number, lat: number) => void;
  onPolygonVertexRemove: (i: number) => void;
  onPolygonDoubleClick: (id: string) => void;
  // All saved avoid polygons:
  avoidPolygons: AvoidPolygon[];
}

export default function MapView(props: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const routeLayersRef = useRef<L.Polyline[]>([]);
  const markersRef = useRef<L.Marker[]>([]);
  const pickModeRef = useRef(props.pickMode);
  const dragHandlerRef = useRef(props.onWaypointDragEnd);
  const pickHandlerRef = useRef(props.onPick);
  const routeClickRef = useRef(props.onRouteClick);
  const drawPolygonModeRef = useRef(props.drawPolygonMode);
  const polygonVertexAddRef = useRef(props.onPolygonVertexAdd);
  const polygonVertexMoveRef = useRef(props.onPolygonVertexMove);
  const polygonVertexRemoveRef = useRef(props.onPolygonVertexRemove);
  const polygonDoubleClickRef = useRef(props.onPolygonDoubleClick);

  // Layer groups
  const editLayerGroupRef = useRef<L.LayerGroup | null>(null);   // drawing/editing vertices
  const savedPolygonsLayerRef = useRef<L.LayerGroup | null>(null); // saved static polygons

  // Keep refs current
  pickModeRef.current = props.pickMode;
  dragHandlerRef.current = props.onWaypointDragEnd;
  pickHandlerRef.current = props.onPick;
  routeClickRef.current = props.onRouteClick;
  drawPolygonModeRef.current = props.drawPolygonMode;
  polygonVertexAddRef.current = props.onPolygonVertexAdd;
  polygonVertexMoveRef.current = props.onPolygonVertexMove;
  polygonVertexRemoveRef.current = props.onPolygonVertexRemove;
  polygonDoubleClickRef.current = props.onPolygonDoubleClick;

  // ── Init map ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView([50.0, 12.0], 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => {
      // Single click: only for waypoint pick mode (NOT polygon drawing)
      if (drawPolygonModeRef.current) return;
      if (!pickModeRef.current) return;
      pickHandlerRef.current(+e.latlng.lng.toFixed(6), +e.latlng.lat.toFixed(6));
    });
    map.on('dblclick', (e: L.LeafletMouseEvent) => {
      // Double click: add polygon vertex in draw mode
      if (!drawPolygonModeRef.current) return;
      L.DomEvent.stopPropagation(e as unknown as Event);
      polygonVertexAddRef.current(+e.latlng.lng.toFixed(6), +e.latlng.lat.toFixed(6));
    });
    editLayerGroupRef.current = L.layerGroup().addTo(map);
    savedPolygonsLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
  }, []);

  // ── Cursor + double-click zoom ────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    el.style.cursor = props.drawPolygonMode ? 'crosshair' : '';
    // Disable Leaflet's built-in double-click zoom while drawing so it doesn't
    // interfere with vertex placement.
    const map = mapRef.current; if (!map) return;
    if (props.drawPolygonMode) {
      map.doubleClickZoom.disable();
    } else {
      map.doubleClickZoom.enable();
    }
  }, [props.drawPolygonMode]);

  // ── Saved polygons (static display) ───────────────────────────────────────
  useEffect(() => {
    const lg = savedPolygonsLayerRef.current; if (!lg) return;
    lg.clearLayers();
    props.avoidPolygons.forEach((p, idx) => {
      if (p.id === props.editingPolygonId) return;
      if (p.vertices.length < 3) return;
      const latlngs = p.vertices.map(([lon, lat]) => [lat, lon] as L.LatLngExpression);
      const isActive = p.active !== false;
      const poly = L.polygon(latlngs, {
        color: isActive ? '#ef4444' : '#94a3b8',
        weight: isActive ? 2 : 1.5,
        fillColor: isActive ? '#ef4444' : '#94a3b8',
        fillOpacity: isActive ? 0.18 : 0.07,
        dashArray: isActive ? undefined : '5,5',
      }).addTo(lg);
      const labelHtml = `<span style="font-weight:700;font-size:11px;">#${idx + 1}</span> ${p.label || `Polygon ${idx + 1}`}${isActive ? '' : ' <em style="opacity:.6">(off)</em>'}`;
      poly.bindTooltip(labelHtml, {
        permanent: true, direction: 'center',
        className: isActive ? 'polygon-map-label' : 'polygon-map-label polygon-map-label--inactive',
        sticky: false,
      });
      // Double-click → enter edit mode
      poly.on('dblclick', (ev: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(ev as unknown as Event);
        polygonDoubleClickRef.current(p.id);
      });
    });
  }, [props.avoidPolygons, props.editingPolygonId]);

  // ── Drawing / editing preview ──────────────────────────────────────────────
  useEffect(() => {
    const lg = editLayerGroupRef.current; if (!lg) return;
    lg.clearLayers();

    const verts = props.polygonVertices;
    if (verts.length === 0) return;

    // Polygon fill preview (≥3 pts)
    if (verts.length >= 3) {
      L.polygon(verts.map(([lon, lat]) => [lat, lon] as L.LatLngExpression), {
        color: '#ef4444', weight: 2, fillColor: '#ef4444', fillOpacity: 0.15, dashArray: '6,4',
      }).addTo(lg);
    } else if (verts.length === 2) {
      L.polyline(verts.map(([lon, lat]) => [lat, lon] as L.LatLngExpression), {
        color: '#ef4444', weight: 2, dashArray: '6,4',
      }).addTo(lg);
    }

    // Draggable vertex markers — right-click to remove
    verts.forEach(([lon, lat], i) => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:16px;height:16px;
          background:#ef4444;border:2px solid #fff;
          border-radius:50%;cursor:move;
          display:flex;align-items:center;justify-content:center;
          font-size:9px;font-weight:700;color:#fff;line-height:1;
          box-shadow:0 1px 4px rgba(0,0,0,.5);
        " title="Drag to move · Right-click to remove">${i + 1}</div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const m = L.marker([lat, lon], { icon, draggable: true, zIndexOffset: 1000 }).addTo(lg);
      m.on('dragend', (ev: L.LeafletEvent) => {
        const ll = (ev.target as L.Marker).getLatLng();
        polygonVertexMoveRef.current(i, +ll.lng.toFixed(6), +ll.lat.toFixed(6));
      });
      m.on('contextmenu', (ev: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(ev as unknown as Event);
        polygonVertexRemoveRef.current(i);
      });
    });
  }, [props.polygonVertices]);

  // ── Waypoint markers ──────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    props.waypoints.forEach((wp, i) => {
      if (wp.lon === null || wp.lat === null) return;
      const m = L.marker([wp.lat, wp.lon], {
        title: `Point ${i + 1} (drag to move)`, draggable: true, autoPan: true,
      })
        .bindTooltip(String(i + 1), { permanent: true, direction: 'top', className: 'route-label' })
        .addTo(map);
      m.on('dragend', (ev: L.LeafletEvent) => {
        const ll = (ev.target as L.Marker).getLatLng();
        dragHandlerRef.current(i, +ll.lng.toFixed(6), +ll.lat.toFixed(6));
      });
      markersRef.current.push(m);
    });
  }, [props.waypoints]);

  // ── Clear routes on demand ─────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    routeLayersRef.current.forEach(l => map.removeLayer(l));
    routeLayersRef.current = [];
  }, [props.clearSignal]);

  // ── Route polylines ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    routeLayersRef.current.forEach(l => map.removeLayer(l));
    routeLayersRef.current = [];
    const res = props.routesResponse;
    if (!res?.routes?.length) return;
    const units = res?.metadata?.query?.units || 'm';
    res.routes.forEach((route: any, i: number) => {
      const pts = decodePolyline(route.geometry);
      const line = L.polyline(pts, { color: routeColor(i), weight: 5, opacity: 0.55 }).addTo(map);
      const s = route.summary || {};
      const km = distanceToKm(s.distance, units).toFixed(2);
      line.bindPopup(`<b>Route ${i + 1}</b><br>${km} km · ${formatDuration(s.duration)}`);
      line.on('click', () => routeClickRef.current(i));
      routeLayersRef.current.push(line);
    });
  }, [props.routesResponse]);

  // ── Route visibility/selection ─────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    routeLayersRef.current.forEach((line, i) => {
      const visible = props.visibleRoutes[i] !== false;
      const selected = i === props.selectedRouteIdx;
      if (!visible) {
        line.setStyle({ opacity: 0, weight: 0 });
        const el = (line as any).getElement?.() as HTMLElement | undefined;
        if (el) el.style.pointerEvents = 'none';
        return;
      }
      const el = (line as any).getElement?.() as HTMLElement | undefined;
      if (el) el.style.pointerEvents = '';
      line.setStyle({ color: routeColor(i), weight: selected ? 7 : 4, opacity: selected ? 1 : 0.5 });
      if (selected) line.bringToFront();
    });
  }, [props.visibleRoutes, props.selectedRouteIdx, props.routesResponse]);

  // ── Matrix lines ───────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    routeLayersRef.current.forEach(l => map.removeLayer(l));
    routeLayersRef.current = [];
    const res = props.matrixResponse;
    if (!res?.sources || !res?.destinations) return;
    res.sources.forEach((s: any) => {
      if (!s?.location) return;
      res.destinations.forEach((d: any) => {
        if (!d?.location) return;
        if (s.location.toString() === d.location.toString()) return;
        const line = L.polyline([
          [s.location[1], s.location[0]], [d.location[1], d.location[0]],
        ], { color: '#38bdf8', weight: 1.2, opacity: 0.4, dashArray: '4,4' }).addTo(map);
        routeLayersRef.current.push(line);
      });
    });
  }, [props.matrixResponse]);

  // ── Fit bounds ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (props.fitBoundsSignal === 0) return;
    const map = mapRef.current; if (!map) return;
    const bounds = L.latLngBounds([]);
    routeLayersRef.current.forEach(l => {
      const b = (l as any).getBounds ? (l as any).getBounds() : null;
      if (b) bounds.extend(b);
    });
    if (!bounds.isValid()) {
      props.waypoints.forEach(wp => {
        if (wp.lon !== null && wp.lat !== null) bounds.extend([wp.lat, wp.lon]);
      });
    }
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
  }, [props.fitBoundsSignal]);

  return <div ref={containerRef} className="map-el" />;
}

